/**
 * ServicesManager — Admin CRUD for appointment types (services).
 * Table view with create/edit modal using standard Modal component.
 */

import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Clock, Phone, Video } from 'lucide-react';
import { Modal } from './Modal';

interface Service {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  buffer_before_min: number;
  buffer_after_min: number;
  color: string;
  session_methods: string[];
  min_advance_hours: number;
  is_active: boolean;
  audiences: string[];
  assigned_coaches: string[];
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const EMPTY_SERVICE = {
  name: '',
  description: '',
  duration_minutes: 30,
  buffer_before_min: 5,
  buffer_after_min: 10,
  color: '#4f46e5',
  session_methods: ['phone'] as string[],
  min_advance_hours: 24,
  audiences: ['client'] as string[],
};

const METHOD_OPTIONS = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'virtual', label: 'Virtual Meeting', icon: Video },
];

// HSL to hex conversion
function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Generate honeycomb hex color grid — hue wheel with lightness rings
function generateHexGrid(): { colors: string[][]; offsets: boolean[] } {
  // Diamond shape: row cell counts and whether row is offset
  const structure = [
    { count: 7, offset: true },
    { count: 8, offset: false },
    { count: 9, offset: true },
    { count: 10, offset: false },
    { count: 11, offset: true },
    { count: 10, offset: false },
    { count: 9, offset: true },
    { count: 8, offset: false },
    { count: 7, offset: true },
  ];
  const centerR = 4;
  const maxHalf = 5.5;
  const colors: string[][] = [];
  const offsets: boolean[] = [];

  for (let r = 0; r < structure.length; r++) {
    const { count, offset } = structure[r];
    const row: string[] = [];
    offsets.push(offset);
    for (let c = 0; c < count; c++) {
      const nx = (c - (count - 1) / 2) / maxHalf;
      const ny = (r - centerR) / (structure.length / 2);
      const dist = Math.sqrt(nx * nx + ny * ny);
      // Rotate so red is at top, hues go clockwise
      const angle = (Math.atan2(nx, -ny) * 180 / Math.PI + 360) % 360;

      if (dist < 0.18) {
        row.push('#ffffff');
      } else {
        const hue = angle;
        // Saturation ramps up from center, lightness decreases outward
        const sat = Math.min(100, 30 + dist * 90);
        const light = Math.max(20, 80 - dist * 60);
        row.push(hslToHex(hue, sat, light));
      }
    }
    colors.push(row);
  }

  // Grayscale row
  colors.push(['#ffffff', '#e0e0e0', '#b0b0b0', '#888888', '#606060', '#404040', '#202020']);
  offsets.push(true);

  return { colors, offsets };
}

const HEX_GRID = generateHexGrid();

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export function ServicesManager() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [form, setForm] = useState(EMPTY_SERVICE);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingService, setDeletingService] = useState<Service | null>(null);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch('/api/services', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load services');
      const data = await res.json();
      setServices(data.services);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openCreate = () => {
    setEditingService(null);
    setForm(EMPTY_SERVICE);
    setShowModal(true);
  };

  const openEdit = (service: Service) => {
    setEditingService(service);
    setForm({
      name: service.name,
      description: service.description,
      duration_minutes: service.duration_minutes,
      buffer_before_min: service.buffer_before_min,
      buffer_after_min: service.buffer_after_min,
      color: service.color,
      session_methods: [...service.session_methods],
      min_advance_hours: service.min_advance_hours,
      audiences: [...(service.audiences || ['client'])],
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const url = editingService ? `/api/services/${editingService.id}` : '/api/services';
      const method = editingService ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setShowModal(false);
      fetchServices();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (service: Service) => {
    try {
      const res = await fetch(`/api/services/${service.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !service.is_active })
      });
      if (!res.ok) throw new Error('Toggle failed');
      fetchServices();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!deletingService) return;
    try {
      const res = await fetch(`/api/services/${deletingService.id}?hard=true`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Delete failed');
      setShowDeleteModal(false);
      setDeletingService(null);
      fetchServices();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleMethod = (method: string) => {
    setForm(prev => ({
      ...prev,
      session_methods: prev.session_methods.includes(method)
        ? prev.session_methods.filter(m => m !== method)
        : [...prev.session_methods, method]
    }));
  };

  const formatDuration = (mins: number, bufBefore: number, bufAfter: number) => {
    const total = mins + bufBefore + bufAfter;
    return `${mins}min (${total}min with buffers)`;
  };

  if (loading) return <div className="services-loading">Loading services...</div>;

  return (
    <div className="services-manager">
      <div className="services-header">
        <div>
          <h1>Appointment Services</h1>
          <p className="services-subtitle">Define the types of appointments clients can book</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Service
        </button>
      </div>

      {error && <div className="services-error">{error}</div>}

      <div className="services-grid">
        {services.map(svc => (
          <div key={svc.id} className={`service-card ${!svc.is_active ? 'inactive' : ''}`}>
            <div className="service-card-header">
              <div className="service-color" style={{ background: svc.color }} />
              <div className="service-info">
                <div className="service-name">{svc.name}</div>
                {svc.description && <div className="service-desc">{svc.description}</div>}
              </div>
              <div className="service-actions">
                <button className="icon-btn" onClick={() => openEdit(svc)} title="Edit">
                  <Edit2 size={14} />
                </button>
                <button className="icon-btn" onClick={() => handleToggleActive(svc)} title={svc.is_active ? 'Deactivate' : 'Activate'}>
                  {svc.is_active ? <ToggleRight size={16} color="#10b981" /> : <ToggleLeft size={16} />}
                </button>
                <button className="icon-btn danger" onClick={() => { setDeletingService(svc); setShowDeleteModal(true); }} title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="service-card-body">
              <div className="service-detail">
                <Clock size={14} />
                {formatDuration(svc.duration_minutes, svc.buffer_before_min, svc.buffer_after_min)}
              </div>
              <div className="service-detail">
                {svc.session_methods.map(m => (
                  <span key={m} className="method-badge">
                    {m === 'phone' ? <Phone size={12} /> : <Video size={12} />}
                    {m}
                  </span>
                ))}
              </div>
              <div className="service-meta">
                {(svc.audiences || []).map(a => (
                  <span key={a} className={`audience-badge ${a}`}>{a}</span>
                ))}
                {(!svc.audiences || svc.audiences.length === 0) && <span className="audience-badge internal">internal</span>}
                <span className="advance-notice">{svc.min_advance_hours}h advance notice</span>
              </div>
            </div>
          </div>
        ))}

        {services.length === 0 && (
          <div className="services-empty">
            No services defined yet. Create your first appointment type to get started.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingService ? 'Edit Service' : 'New Service'}
        size="medium"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name || form.session_methods.length === 0}>
              {saving ? 'Saving...' : editingService ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <div className="service-form">
          <div className="form-group">
            <label>Name *</label>
            <input type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Weekly Check-in" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this appointment type" rows={2} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Duration (minutes) *</label>
              <input type="number" min={1} value={form.duration_minutes} onChange={e => setForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 1 }))} />
            </div>
            <div className="form-group">
              <label>Buffer Before (min)</label>
              <input type="number" min={0} value={form.buffer_before_min} onChange={e => setForm(prev => ({ ...prev, buffer_before_min: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="form-group">
              <label>Buffer After (min)</label>
              <input type="number" min={0} value={form.buffer_after_min} onChange={e => setForm(prev => ({ ...prev, buffer_after_min: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="form-group">
            <label>Min Advance Notice (hours)</label>
            <input type="number" min={0} value={form.min_advance_hours} onChange={e => setForm(prev => ({ ...prev, min_advance_hours: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="form-group">
            <label>Session Methods *</label>
            <div className="method-toggles">
              {METHOD_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = form.session_methods.includes(opt.value);
                return (
                  <button key={opt.value} className={`method-toggle ${active ? 'active' : ''}`} onClick={() => toggleMethod(opt.value)} type="button">
                    <Icon size={14} /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="form-group">
            <label>Color</label>
            <button
              type="button"
              className="color-trigger-btn"
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              <span className="color-trigger-dot" style={{ background: form.color }} />
              <span className="color-trigger-label">{form.color}</span>
              <span className={`color-trigger-arrow ${showColorPicker ? 'open' : ''}`}>&#9662;</span>
            </button>
            {showColorPicker && (
              <div className="color-picker-dropdown">
                <div className="hex-grid">
                  {HEX_GRID.colors.map((row, ri) => (
                    <div key={ri} className={`hex-row ${HEX_GRID.offsets[ri] ? 'offset' : ''} ${ri === HEX_GRID.colors.length - 1 ? 'grayscale-row' : ''}`}>
                      {row.map((c, ci) => (
                        <button
                          key={`${ri}-${ci}`}
                          className={`hex-cell ${form.color.toLowerCase() === c.toLowerCase() ? 'selected' : ''}`}
                          style={{ background: c }}
                          onClick={() => setForm(prev => ({ ...prev, color: c }))}
                          type="button"
                          title={c}
                        />
                      ))}
                    </div>
                  ))}
                </div>
                <div className="color-picker-footer">
                  <input
                    type="text"
                    className="color-hex-input"
                    value={form.color}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setForm(prev => ({ ...prev, color: v }));
                    }}
                    placeholder="#4f46e5"
                  />
                  <label className="custom-color-btn" title="Custom color">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    />
                    <span className="rainbow-icon" />
                    <span className="custom-label">Custom</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Audiences</label>
            <div className="method-toggles">
              {[{ value: 'public', label: 'Public (website visitors)' }, { value: 'client', label: 'Client (logged-in clients)' }].map(opt => {
                const active = form.audiences.includes(opt.value);
                return (
                  <button key={opt.value} className={`method-toggle ${active ? 'active' : ''}`} onClick={() => setForm(prev => ({
                    ...prev,
                    audiences: active ? prev.audiences.filter(a => a !== opt.value) : [...prev.audiences, opt.value]
                  }))} type="button">
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              {form.audiences.length === 0 ? 'Internal only (staff use)' : ''}
            </span>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeletingService(null); }}
        title="Delete Service"
        variant="danger"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setShowDeleteModal(false); setDeletingService(null); }}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete Permanently</button>
          </>
        }
      >
        <p>Permanently delete <strong>{deletingService?.name}</strong>? This cannot be undone. Consider deactivating instead.</p>
      </Modal>

      <style>{`
        .services-manager { max-width: 1000px; }
        .services-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 24px;
        }
        .services-header h1 { color: var(--text-primary); margin: 0 0 4px; font-size: 24px; }
        .services-subtitle { color: var(--text-secondary); margin: 0; font-size: 14px; }
        .services-error {
          background: #ef444415; border: 1px solid #ef444440; border-radius: var(--radius-medium);
          padding: 12px 16px; color: #ef4444; margin-bottom: 16px; font-size: 14px;
        }
        .services-loading { padding: 40px; color: var(--text-secondary); text-align: center; }
        .services-empty {
          padding: 60px 40px; text-align: center; color: var(--text-tertiary);
          background: var(--bg-primary); border: 2px dashed var(--border-secondary); border-radius: var(--radius-large);
        }
        .services-grid { display: flex; flex-direction: column; gap: 12px; }
        .service-card {
          background: var(--bg-primary); border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large); overflow: hidden; transition: opacity 0.2s;
        }
        .service-card.inactive { opacity: 0.5; }
        .service-card-header {
          display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px;
          border-bottom: 1px solid var(--border-secondary);
        }
        .service-color { width: 4px; align-self: stretch; border-radius: 2px; flex-shrink: 0; }
        .service-info { flex: 1; min-width: 0; }
        .service-name { font-weight: 600; color: var(--text-primary); font-size: 15px; }
        .service-desc { color: var(--text-secondary); font-size: 13px; margin-top: 2px; }
        .service-actions { display: flex; gap: 4px; flex-shrink: 0; }
        .icon-btn {
          background: none; border: none; padding: 6px; border-radius: var(--radius-medium);
          color: var(--text-tertiary); cursor: pointer; transition: all 0.15s;
        }
        .icon-btn:hover { background: var(--bg-secondary); color: var(--text-primary); }
        .icon-btn.danger:hover { color: #ef4444; }
        .service-card-body { padding: 12px 20px; display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
        .service-detail {
          display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-secondary);
        }
        .method-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 500;
          background: var(--bg-secondary); color: var(--text-secondary);
        }
        .service-meta { display: flex; gap: 8px; margin-left: auto; }
        .audience-badge {
          font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500;
        }
        .audience-badge.client { background: #10b98115; color: #10b981; }
        .audience-badge.public { background: #3b82f615; color: #3b82f6; }
        .audience-badge.internal { background: #f59e0b15; color: #f59e0b; }
        .advance-notice { font-size: 12px; color: var(--text-tertiary); }

        /* Form styles */
        .service-form { display: flex; flex-direction: column; gap: 16px; }
        .form-group { display: flex; flex-direction: column; gap: 6px; }
        .form-group label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
        .form-group input, .form-group textarea, .form-group select {
          padding: 8px 12px; border: 1px solid var(--border-secondary); border-radius: var(--radius-medium);
          background: var(--bg-secondary); color: var(--text-primary); font-size: 14px;
        }
        .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
          outline: none; border-color: var(--accent-blue);
        }
        .form-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
        .method-toggles { display: flex; gap: 8px; }
        .method-toggle {
          display: flex; align-items: center; gap: 6px; padding: 8px 14px;
          border: 1px solid var(--border-secondary); border-radius: var(--radius-medium);
          background: var(--bg-secondary); color: var(--text-secondary); cursor: pointer;
          font-size: 13px; transition: all 0.15s;
        }
        .method-toggle.active { border-color: var(--accent-blue); background: var(--accent-blue)15; color: var(--accent-blue); }
        .color-trigger-btn {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 12px; border-radius: var(--radius-medium);
          border: 1px solid var(--border-secondary); background: var(--bg-secondary);
          cursor: pointer; transition: all 0.15s; width: 100%;
        }
        .color-trigger-btn:hover { border-color: var(--text-tertiary); background: var(--bg-tertiary); }
        .color-trigger-dot {
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          border: 2px solid rgba(255,255,255,0.15); box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }
        .color-trigger-label { font-family: monospace; font-size: 13px; color: var(--text-secondary); }
        .color-trigger-arrow {
          margin-left: auto; font-size: 12px; color: var(--text-tertiary);
          transition: transform 0.2s;
        }
        .color-trigger-arrow.open { transform: rotate(180deg); }

        .color-picker-dropdown {
          margin-top: 8px; padding: 10px; border-radius: var(--radius-medium);
          border: 1px solid var(--border-secondary); background: var(--bg-secondary);
          animation: colorPickerSlide 0.15s ease-out;
        }
        @keyframes colorPickerSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .hex-grid {
          display: flex; flex-direction: column; align-items: center;
        }
        .hex-row {
          display: flex; gap: 0; margin-top: -9px;
        }
        .hex-row:first-child { margin-top: 0; }
        .hex-row.grayscale-row { margin-top: 8px; }
        .hex-cell {
          width: 26px; height: 30px; border: none; padding: 0;
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
          cursor: pointer; transition: transform 0.12s, filter 0.12s;
          position: relative; flex-shrink: 0;
        }
        .hex-cell:hover {
          transform: scale(1.4); z-index: 3;
          filter: brightness(1.15);
        }
        .hex-cell.selected {
          transform: scale(1.35); z-index: 2;
          filter: brightness(1.2) drop-shadow(0 0 5px rgba(255,255,255,0.7));
        }

        .color-picker-footer {
          display: flex; align-items: center; gap: 10px; margin-top: 10px;
          padding-top: 10px; border-top: 1px solid var(--border-secondary);
        }
        .color-hex-input {
          font-family: monospace; font-size: 13px; padding: 5px 8px; border-radius: var(--radius-small);
          border: 1px solid var(--border-secondary); background: var(--bg-primary); color: var(--text-primary); width: 85px;
        }
        .custom-color-btn {
          position: relative; display: flex; align-items: center; gap: 6px;
          cursor: pointer; margin-left: auto; padding: 4px 10px 4px 6px;
          border-radius: var(--radius-medium); border: 1px solid var(--border-secondary);
          background: var(--bg-primary); transition: all 0.15s;
        }
        .custom-color-btn:hover { border-color: var(--text-tertiary); background: var(--bg-tertiary); }
        .rainbow-icon {
          width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
          background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
        }
        .custom-label { font-size: 12px; color: var(--text-secondary); }

        .btn { padding: 8px 16px; border-radius: var(--radius-medium); font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; }
        .btn-primary { background: var(--accent-blue); color: white; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { background: var(--bg-secondary); color: var(--text-secondary); border: 1px solid var(--border-secondary); }
        .btn-secondary:hover { background: var(--bg-tertiary); }
        .btn-danger { background: #ef4444; color: white; }
        .btn-danger:hover { opacity: 0.9; }
      `}</style>
    </div>
  );
}
